'use client'

import React, { useCallback, useEffect, useState } from 'react'
import { Link } from '@/lib'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Palette, Briefcase, ArrowRight, CaretLeft, CaretRight } from 'phosphor-react'
import Components from '../components'

const INTRO_SLIDES = [
  {
    title: 'Collaboration, empathy, and trust',
    quote:
      "Hi there, I'm Dixon Bordiano. I'm a UX advocate and Product Design leader — someone who believes great design is built on collaboration, empathy, and trust. For over 12 years, I've focused on creating not just better products, but better ways of working.",
  },
  {
    title: 'Building a UX practice from scratch',
    quote:
      'I have worked on large, mature design teams and built UX practices from the ground up. At Cognira, I championed continuous discovery, open feedback, and a shared design system to make the first product releases a success.',
  },
  {
    title: 'Great design takes teamwork',
    quote:
      "Before that, I worked on unifying UX across global retail platforms. Great design takes teamwork. It's about listening — to users, stakeholders, or the quietest voice in the room. The best solutions come from diverse perspectives and a shared commitment to solving real problems.",
  },
  {
    title: 'Designing with integrity',
    quote:
      "What matters most to me? Designing with integrity — advocating for ethical AI, prioritizing accessibility, and creating experiences that respect users' time. And building teams where people feel valued, heard, and excited to come to work.",
  },
  {
    title: "Let's connect",
    quote:
      "If you're looking for someone who can lead with empathy, scale collaboration, and turn complex challenges into simple, human-centered solutions, let's connect.",
  },
] as const

export default function Home() {
  const [slideIndex, setSlideIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  const nextSlide = useCallback(() => {
    setSlideIndex((i) => (i + 1) % INTRO_SLIDES.length)
  }, [])

  const prevSlide = useCallback(() => {
    setSlideIndex((i) => (i - 1 + INTRO_SLIDES.length) % INTRO_SLIDES.length)
  }, [])

  useEffect(() => {
    if (paused) return
    const id = window.setInterval(nextSlide, 7000)
    return () => window.clearInterval(id)
  }, [paused, nextSlide])

  const slide = INTRO_SLIDES[slideIndex]

  return (
    <Components.PageContainer>
      <Components.SkipLink />
      <Components.GlassNavigation />
      
      <main id="main-content" tabIndex={-1} className="pt-16 md:pt-32 pb-28 md:pb-16 px-4 md:px-6" role="main" aria-label="Main content">
        <div className="max-w-7xl mx-auto">
          <header className="text-center mb-12 md:mb-20">
            <p className="brutal-label mb-3">Portfolio</p>
            <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold text-[var(--brutal-ink)] mb-4 md:mb-6 leading-tight break-words px-4 tracking-tight">
              Dixon Bordiano
            </h1>
            <p className="text-xl md:text-2xl lg:text-3xl text-[var(--brutal-ink)] mb-4 md:mb-6 font-semibold break-words px-4">
              Creative & Product Design Leader
            </p>
            <p className="text-base md:text-lg lg:text-xl text-[var(--brutal-muted)] max-w-3xl mx-auto leading-relaxed break-words px-4">
              Transforming complex challenges into meaningful experiences through design, creativity, and leadership
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-16 max-w-5xl mx-auto">
            <Card className="brutal-panel p-8 md:p-12 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_#141414] transition-all duration-150 group" role="article" aria-labelledby="professional-projects-heading">
              <div className="flex flex-col items-center text-center h-full">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[var(--brutal-mustard)] border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] flex items-center justify-center mb-6 group-hover:-translate-y-0.5 transition-transform duration-150" aria-hidden="true">
                  <Briefcase weight="duotone" className="w-8 h-8 md:w-10 md:h-10 text-[var(--brutal-ink)]" aria-hidden="true" />
                </div>
                <h2 id="professional-projects-heading" className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4 break-words">
                  Professional Projects
                </h2>
                <p className="text-[var(--brutal-muted)] text-base md:text-lg mb-6 flex-grow break-words">
                  Enterprise SaaS solutions, AI/ML design, design systems, and strategic product leadership
                </p>
                <Button
                  asChild
                  className="w-full brutal-btn"
                >
                  <Link to="/ProfessionalProjects">
                    View Professional Work
                    <ArrowRight weight="duotone" className="ml-2 w-4 h-4" aria-hidden="true" />
                  </Link>
                </Button>
                <p className="brutal-label mt-4 break-words">
                  Password protected
                </p>
              </div>
            </Card>
            
            <Card className="brutal-panel p-8 md:p-12 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_#141414] transition-all duration-150 group" role="article" aria-labelledby="creative-projects-heading">
              <div className="flex flex-col items-center text-center h-full">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[var(--brutal-mustard)] border-2 border-[var(--brutal-ink)] shadow-[var(--brutal-shadow-sm)] flex items-center justify-center mb-6 group-hover:-translate-y-0.5 transition-transform duration-150 flex-shrink-0" aria-hidden="true">
                  <Palette weight="duotone" className="w-8 h-8 md:w-10 md:h-10 text-[var(--brutal-ink)]" aria-hidden="true" />
                </div>
                <h2 id="creative-projects-heading" className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4 break-words">
                  Creative Projects
                </h2>
                <p className="text-[var(--brutal-muted)] text-base md:text-lg mb-6 flex-grow break-words">
                  Personal creative work, volunteer projects, and passion-driven design explorations
                </p>
                <Button
                  asChild
                  className="w-full brutal-btn-ghost"
                >
                  <Link to="/CreativeProjects">
                    View Creative Work
                    <ArrowRight weight="duotone" className="ml-2 w-4 h-4" aria-hidden="true" />
                  </Link>
                </Button>
                <p className="brutal-label mt-4 break-words">
                  Open access
                </p>
              </div>
            </Card>
          </div>

          <section className="max-w-5xl mx-auto" aria-labelledby="about-heading">
            <Card
              className="brutal-panel overflow-hidden !p-0 !gap-0 relative"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              <div className="relative bg-[var(--brutal-panel)]">
                {/* Neo-brutalist accents — md+ only; they collide with copy/controls on small screens */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 overflow-hidden"
                >
                  <div className="absolute -top-6 -right-6 hidden h-28 w-28 rotate-12 border-2 border-[var(--brutal-ink)] bg-[var(--brutal-mustard)] md:block" />
                  <div className="absolute top-16 -left-4 hidden h-16 w-16 -rotate-6 border-2 border-[var(--brutal-ink)] bg-[var(--brutal-accent)] md:block" />
                  <div className="absolute bottom-10 right-10 hidden h-3 w-24 border-2 border-[var(--brutal-ink)] bg-[var(--brutal-kraft)] md:block" />
                </div>

                <div className="relative px-5 pt-6 pb-5 md:px-16 md:pt-10 md:pb-8">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={prevSlide}
                    className="absolute left-3 top-1/2 -translate-y-1/2 brutal-btn !rounded-full w-10 h-10 z-10 hidden md:inline-flex"
                    aria-label="Previous quote"
                  >
                    <CaretLeft weight="bold" className="w-5 h-5" />
                  </Button>

                  <div className="relative z-10 min-w-0">
                    <p className="brutal-label mb-3">A bit about me</p>
                    <h2
                      id="about-heading"
                      key={`title-${slideIndex}`}
                      className="text-xl md:text-2xl lg:text-3xl font-bold text-[var(--brutal-ink)] mb-4 leading-snug break-words text-balance"
                    >
                      {slide.title}
                    </h2>
                    <p
                      key={`quote-${slideIndex}`}
                      className="text-[var(--brutal-muted)] text-sm md:text-base leading-relaxed break-words max-w-3xl"
                    >
                      {slide.quote}
                    </p>

                    <div className="mt-6 hidden md:flex items-center gap-3">
                      <p className="brutal-label shrink-0">
                        {slideIndex + 1} / {INTRO_SLIDES.length}
                      </p>
                      <div className="flex gap-2 min-w-0">
                        {INTRO_SLIDES.map((_, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => setSlideIndex(index)}
                            aria-label={`Go to slide ${index + 1}`}
                            aria-current={index === slideIndex ? true : undefined}
                            className={`h-2.5 w-2.5 shrink-0 rounded-full border-2 border-[var(--brutal-ink)] transition-colors ${
                              index === slideIndex
                                ? 'bg-[var(--brutal-mustard)]'
                                : 'bg-[var(--brutal-panel)]'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={nextSlide}
                    className="absolute right-3 top-1/2 -translate-y-1/2 brutal-btn !rounded-full w-10 h-10 z-10 hidden md:inline-flex"
                    aria-label="Next quote"
                  >
                    <CaretRight weight="bold" className="w-5 h-5" />
                  </Button>
                </div>

                {/* Full-bleed mobile controls — no negative-margin hacks */}
                <div className="flex md:hidden items-center gap-3 border-t-2 border-[var(--brutal-ink)] bg-[var(--brutal-mustard)] px-5 py-3 w-full">
                  <p className="brutal-label shrink-0">
                    {slideIndex + 1} / {INTRO_SLIDES.length}
                  </p>
                  <div className="flex gap-2 min-w-0">
                    {INTRO_SLIDES.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setSlideIndex(index)}
                        aria-label={`Go to slide ${index + 1}`}
                        aria-current={index === slideIndex ? true : undefined}
                        className={`h-2.5 w-2.5 shrink-0 rounded-full border-2 border-[var(--brutal-ink)] transition-colors ${
                          index === slideIndex
                            ? 'bg-[var(--brutal-ink)]'
                            : 'bg-[var(--brutal-panel)]'
                        }`}
                      />
                    ))}
                  </div>
                  <div className="flex gap-2 ml-auto shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={prevSlide}
                      className="brutal-btn !rounded-full w-9 h-9"
                      aria-label="Previous quote"
                    >
                      <CaretLeft weight="bold" className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={nextSlide}
                      className="brutal-btn !rounded-full w-9 h-9"
                      aria-label="Next quote"
                    >
                      <CaretRight weight="bold" className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </section>
        </div>
      </main>
    </Components.PageContainer>
  )
}
